<template>
  <div class="v3-widget v3-panel" v-testid="testid">
    <div v-if="title" class="v3-panel-head">
      <h3 v-testid="testid + '-title'">{{ title }}</h3>
      <span v-if="ps.total.value" class="v3-count">{{ ps.total.value }}</span>
    </div>

    <V3Skeleton v-if="ps.isLoading.value && !ps.hasItems.value" shape="panel" :heading="false" />
    <div v-else-if="!ps.hasItems.value" class="v3-pstack-empty" v-testid="testid + '-empty'">
      <span class="v3-pcard-ic v3-pcard-ic--ok"><span class="ms">task_alt</span></span>
      <div class="v3-pcard-body">
        <div class="v3-pcard-title">You&rsquo;re all caught up</div>
        <div class="v3-pcard-line">Nothing needs you right now. New levelling work lands here the moment it&rsquo;s assigned to you.</div>
      </div>
      <span class="v3-pcard-cta">
        <button class="v3-tt-btn" type="button" v-testid="testid + '-view-jobs'" @click="viewMyJobs">View my jobs</button>
      </span>
    </div>

    <template v-else>
      <button v-if="ps.showAll.value" class="v3-viewall" type="button" v-testid="testid + '-show-less'" @click="ps.showAll.value = false">
        Show less
      </button>

      <div class="v3-pstack">
        <div
          v-for="c in ps.shown.value"
          :key="c.id"
          class="v3-pcard"
          :class="[`v3-pcard--${c.tone}`, { 'is-lead': c.lead }]"
          role="button"
          tabindex="0"
          v-testid="testid + '-row-' + c.id"
          @click="open(c)"
        >
          <span class="v3-pcard-ic" :class="`v3-pcard-ic--${c.tone}`"><span class="ms">{{ c.icon }}</span></span>
          <div class="v3-pcard-id">
            <div class="v3-pcard-title">{{ c.title }}</div>
            <span v-if="c.type" class="v3-chip v3-chip--type">{{ c.type }}</span>
            <div v-if="c.meta" class="v3-pcard-meta">{{ c.meta }}</div>
          </div>
          <div class="v3-pcard-ctx">
            <div class="v3-pcard-state">
              <span class="v3-stepper">
                <span class="v3-stepper-track">
                  <span
                    v-for="si in c.stepCount"
                    :key="si"
                    class="v3-stepper-seg"
                    :class="si - 1 <= c.stepIdx ? `is-done v3-stepper-seg--${c.stepTone}` : ''"
                  ></span>
                </span>
              </span>
              <span class="v3-spill2" :class="`v3-spill2--${c.pillTone}`"><span class="ms">{{ c.pillIcon }}</span>{{ c.pillLabel }}</span>
            </div>
            <div v-if="c.feedback" class="v3-pcard-fb" :class="{ 'is-open': openFb[c.id] }">
              <span ref="fbtext" :data-fbid="c.id" class="v3-pcard-fbtext"><strong>{{ c.feedbackBy }}:</strong> {{ c.feedback }}</span>
              <button
                v-if="fbOverflow[c.id] || openFb[c.id]"
                class="v3-pcard-fbmore"
                type="button"
                v-testid="testid + '-row-' + c.id + '-feedback-toggle'"
                @click.stop="$set(openFb, c.id, !openFb[c.id])"
              >{{ openFb[c.id] ? 'Show less' : 'Show more' }}</button>
            </div>
            <div v-else-if="c.resumeLine" class="v3-pcard-line">{{ c.resumeLine }}</div>
          </div>
          <div class="v3-pcard-cta">
            <button class="v3-tt-btn" :class="{ 'v3-tt-btn--lead': c.lead }" type="button" @click.stop="open(c)">
              {{ c.cta }}
            </button>
          </div>
        </div>
      </div>

      <button v-if="ps.hasMore.value" class="v3-viewall" type="button" v-testid="testid + '-view-all'" @click="ps.showAll.value = !ps.showAll.value">
        {{ ps.showAll.value ? 'Show less' : 'View all (' + ps.total.value + ')' }}
      </button>
    </template>
  </div>
</template>

<script>
import { getCurrentInstance } from "vue";
import { priorityStack } from "@rm-ui/homepage/priorityStack";
import V3Skeleton from "./cLandingV3Skeleton";

// New-portal (v3) PriorityStack — the manager's own "what needs me now" stack
// (sent-back → in-progress → ready) as full-width horizontal cards with a
// lifecycle stepper, status pill, inline sent-back feedback and a type-aware
// CTA. Thin Vue 2 presentation over the shared `priorityStack` composable; tasks
// come from the Vuex tasks store (same source as TaskList). Matches the
// prototype V3PriorityStack. Sibling: rm-portal-beta PriorityStackWidget.vue.
export default {
  name: "cLandingV3PriorityStack",
  components: { V3Skeleton },
  props: {
    definition: { type: Object, default: () => ({}) },
    editMode: { type: Boolean, default: false },
    qaActions: { type: Array, default: () => [] },
  },
  setup(props) {
    const store = getCurrentInstance().proxy.$store;
    const ps = priorityStack({
      getTasks: () => store.state.tasks.taskList,
      getLoading: () => store.state.tasks.isLoading,
      getLimit: () => props.definition.limit || 3,
    });
    return { ps };
  },
  data() {
    return { openFb: {}, fbOverflow: {} };
  },
  computed: {
    testid() {
      return this.definition.controlType || "v3PriorityStack";
    },
    title() {
      return this.definition.title === undefined ? "My Priorities" : this.definition.title;
    },
  },
  created() {
    this.$store.dispatch("tasks/init");
  },
  mounted() {
    this.measureFeedback();
  },
  updated() {
    this.measureFeedback();
  },
  methods: {
    // Prototype-parity overflow detection: measure whether each clamped
    // feedback text exceeds its 2-line box (only then show "Show more").
    measureFeedback() {
      const els = this.$refs.fbtext;
      const list = Array.isArray(els) ? els : els ? [els] : [];
      list.forEach((el) => {
        const id = el.getAttribute("data-fbid");
        if (id == null || this.openFb[id]) return;
        const over = el.scrollHeight - el.clientHeight > 2;
        if (this.fbOverflow[id] !== over) this.$set(this.fbOverflow, id, over);
      });
    },
    viewMyJobs() {
      if (this.editMode) return;
      this.$router.push({ name: "alljobs" }).catch(() => {});
    },
    open(c) {
      if (this.editMode) return;
      const reference = c.system_number || c.reference;
      if (reference) this.$emit("openDocument", { system_number: reference, reference });
    },
  },
};
</script>

<style lang="scss" src="./v3Widgets.scss"></style>
